/**
 * Contrato de la evaluación práctica (roleplay).
 *
 * Dos capas conviven en este archivo:
 *
 * 1. **DTOs de backend** (al final, sección "Contrato de backend"): la forma
 *    literal que devuelven los endpoints, tal cual la documentó backend. No se
 *    traducen nombres: es lo que se compara contra la doc.
 * 2. **Modelo de UI**: lo que consumen la página y los componentes.
 *
 * `RoleplayService` es el único que conoce las dos y hace el mapeo, así que un
 * cambio de contrato se resuelve en este archivo más esa fachada.
 */

/**
 * Modalidad en la que el usuario responde el roleplay.
 *
 * Las tres primeras son **por turnos**: el usuario produce una respuesta
 * completa (texto o grabación), la sube y espera la del personaje. `streaming` es
 * de otra naturaleza —una llamada en vivo por WebRTC contra OpenAI, en la que
 * el backend no ve el audio y solo recibe el registro de cada intercambio—, así
 * que casi todo lo que la distingue está aislado en `RoleplayStreamService` y en
 * `roleplay-stream-panel`.
 */
export type RoleplayMode = 'chat' | 'voice' | 'video' | 'streaming';

/** Autor de un mensaje de la conversación. */
export type RoleplayAuthor = 'character' | 'user';

/**
 * Estado de envío de un mensaje del usuario.
 * `procesando` es el que pinta la burbuja "Procesando y transcribiendo respuesta…".
 */
export type RoleplayMessageStatus = 'enviando' | 'procesando' | 'enviado' | 'error';

/**
 * Bullets del modal de indicaciones previas.
 *
 * Es **copy de UI**, no datos: no llega en ningún payload. La línea propia de
 * cada modalidad la agrega el modal (`indicacionModalidad`).
 */
export const ROLEPLAY_INDICACIONES: string[] = [
  'Vas a conversar con un personaje simulado que representa una situación real.',
  'No salgas de la aplicación hasta terminar la evaluación. Si sales, se descontará un intento y se calificará hasta donde hayas avanzado.',
];

/**
 * Línea extra de indicaciones propia de cada modalidad (permisos que se van a pedir). `''` para
 * `chat` o una modalidad desconocida. Es la misma frase que ya muestran el modal del tema y el
 * listado de roleplays en sus propios getters.
 */
export function roleplayIndicacionModalidad(modo: string | null | undefined): string {
  switch (modo) {
    case 'voice':
      return 'Necesitarás permiso de micrófono para poder responder.';
    case 'video':
      return 'Necesitarás permiso de cámara y micrófono para poder responder.';
    case 'streaming':
      return 'Vas a conversar en vivo por voz: necesitarás micrófono y un lugar silencioso.';
    default:
      return '';
  }
}

/** Personaje que interpreta la IA durante el roleplay. */
export interface RoleplayCharacter {
  /** Ej: "Carlos Ramírez". */
  nombre: string;
  /** Ej: "Cliente frustrado". */
  rol: string;
  avatar?: string;
}

/** Qué tan exigente es el escenario. */
export type RoleplayDifficulty = 'easy' | 'medium' | 'hard';

/**
 * Configuración del roleplay de un tema: el escenario y el personaje que la IA
 * va a interpretar.
 *
 * Llega por su **propio** endpoint (`GET roleplay/topics/{topic_id}`), aparte de
 * la sesión: es lo que pinta el título de la cabecera y el nombre del evaluador
 * en sus burbujas. Por eso la página la pide en paralelo y muestra skeletons
 * mientras tanto, en vez de retrasar la apertura de la conversación.
 */
export interface RoleplayTopicConfig {
  /** Id del escenario. */
  escenario_id: string;
  /** Título del escenario. Ej: "Objeción de precio". */
  titulo: string;
  /** Situación que se está simulando. */
  contexto: string;
  objetivos: string[];
  dificultad: RoleplayDifficulty;
  /** Personaje que interpreta la IA: es el autor de las burbujas del evaluador. */
  personaje: RoleplayCharacter;
  /** Rasgos de personalidad del personaje. Hoy no se pintan. */
  rasgos: string[];
  /** Modalidad configurada en el tema (`student_mode`). */
  modalidad: RoleplayMode;
  /** La práctica cuenta para la nota del tema. */
  evaluable: boolean;
  /** Cómo se combina con la evaluación teórica. Ej: "averaged_with_exam". */
  modo_calificacion: string;
  /**
   * Topes declarados en el escenario.
   * ⚠️ Referenciales: los que manda la sesión al abrirse (`RoleplaySession`) son
   * los que gobiernan el cronómetro y el contador de turnos.
   */
  max_turns: number;
  max_duration_seconds: number;
  /**
   * Control de abandono de la vista: cambiar de pestaña o mandar la app a
   * segundo plano descuenta un contador y, al agotarlo, cierra la evaluación.
   *
   * Mismas llaves y mismo significado que en `evaluation_settings` de la
   * evaluación teórica, que es de donde viene la funcionalidad. Sin ellas
   * (`false` / `0`) el control queda apagado y la conversación se comporta
   * como antes de existir.
   */
  not_allowed_change_page: boolean;
  quantity_attempts_to_change_page: number;
}

/** Un mensaje de la conversación. */
export interface RoleplayMessage {
  id: string;
  autor: RoleplayAuthor;
  texto: string;
  /** ISO string. */
  created_at: string;
  /** Pinta el chip "Transcripción" en las modalidades de voz y video. */
  es_transcripcion?: boolean;
  /**
   * Audio del mensaje del personaje/evaluador, en las modalidades voz y video:
   * es el `persona_audio_url` que manda la API (se reproduce solo, y el
   * `<audio controls>` de la burbuja queda de respaldo).
   *
   * En esta etapa el usuario NO tiene reproductor de su propia grabación: su
   * burbuja solo muestra la transcripción (`texto` + `es_transcripcion`). El
   * audio que graba se manda a la API para transcribir, pero no se re-escucha
   * desde la burbuja.
   */
  audio_url?: string;
  estado?: RoleplayMessageStatus;
}

/** Sesión completa del roleplay. */
export interface RoleplaySession {
  session_id: string;
  /** Modalidad confirmada por la API (`opening.mode`). */
  mode: RoleplayMode;
  personaje: RoleplayCharacter;
  /** Rol que interpreta el usuario. Ej: "Especialista en Soporte y Retención". */
  user_role: string;
  /**
   * Arranca con el primer turno del personaje. **Vacío en `streaming`**: ahí el
   * saludo no viene en la sesión, lo dice el personaje por el canal en vivo.
   */
  mensajes: RoleplayMessage[];
  interacciones_usadas: number;
  /** Tope de interacciones del usuario. Lo manda la API al abrir la sesión. */
  max_turns: number;
  /** Ventana de tiempo de la conversación, en segundos. La manda la API. */
  max_duration_seconds: number;
  finalizada: boolean;
  /** Por qué cerró la conversación. `null` mientras siga abierta. */
  end_reason: RoleplayEndReason | null;
}

/** Respuesta del usuario en modalidad chat. */
export interface RoleplayTextTurn {
  texto: string;
}

/** Respuesta del usuario en modalidad voz o video. */
export interface RoleplayMediaTurn {
  blob: Blob;
  mimeType: string;
  /** Duración de la grabación en segundos. */
  duracion: number;
}

export type RoleplayTurnPayload = RoleplayTextTurn | RoleplayMediaTurn;

export function isMediaTurn(payload: RoleplayTurnPayload): payload is RoleplayMediaTurn {
  return (payload as RoleplayMediaTurn).blob instanceof Blob;
}

/** Respuesta del servidor tras enviar un turno. */
export interface RoleplayTurnResponse {
  /** El mensaje del usuario ya resuelto (con la transcripción si aplica). */
  mensaje_usuario: RoleplayMessage;
  /** Puede venir null si la conversación terminó con el turno del usuario. */
  mensaje_personaje: RoleplayMessage | null;
  interacciones_usadas: number;
  /** La "llave" que ordena finalizar la conversación (`session_ended`). */
  finalizada: boolean;
  /** Por qué cerró: se agotó el tiempo o las interacciones. */
  end_reason: RoleplayEndReason | null;
}

/**
 * Resultado de la evaluación práctica, ya en forma de UI.
 *
 * Es el payload de `/end` más lo que la pantalla necesita y el DTO trae en otra
 * forma: la transcripción mapeada a burbujas y quién habla en cada una. Lo
 * arma `RoleplayService.finishSession()`, único punto que conoce las dos capas.
 */
export interface RoleplayResult extends RoleplayEndResponseDto {
  /**
   * Transcripción de la conversación en la forma que pintan las burbujas.
   * Vacía mientras backend no mande `transcript` en `/end`.
   */
  mensajes: RoleplayMessage[];
  /** Personaje que interpretó la IA, para el autor de sus burbujas. */
  personaje: RoleplayCharacter;
  /** Rol que interpretó el usuario, para la etiqueta "Tú (…)". */
  userRole: string;
}

/**
 * Llave de `sessionStorage` por la que viaja el resultado hacia la pantalla de
 * resultados. Va por ahí y no por la URL porque la rúbrica completa son varios
 * KB de JSON.
 */
export const ROLEPLAY_RESULT_KEY = 'roleplay_result';

/**
 * Prefijo de la marca "este usuario ya vio la guía de la evaluación", en
 * `localStorage`. Se completa con el id del usuario (`tour_roleplay_123`): la
 * guía es por persona, no por navegador, así que un segundo usuario en el mismo
 * equipo sí la ve.
 */
export const ROLEPLAY_TOUR_KEY = 'tour_roleplay';

/**
 * Tope por defecto de una respuesta grabada (audio o video), en segundos.
 *
 * Se recorta a lo que quede de `RoleplaySession.max_duration_seconds`: si la
 * ventana de la evaluación es más corta que esto, manda la ventana.
 */
export const ROLEPLAY_MAX_RECORDING_SECONDS = 180;

/**
 * Ventana en la que salir de la app NO cuenta como abandono de la evaluación.
 *
 * La abren las acciones que mandan al usuario fuera por decisión de la propia
 * app —pedir cámara o micrófono—: el diálogo del sistema deja la app inactiva
 * y dispara el mismo `appStateChange` que un abandono real. Tres segundos
 * alcanzan para leer el diálogo y aceptar sin que el contador se mueva.
 */
export const ROLEPLAY_ABANDONO_GRACIA_MS = 3000;

/**
 * Ventana en la que dos avisos de abandono seguidos se consideran **el mismo**.
 *
 * En la app híbrida un solo paso a segundo plano dispara las dos fuentes de
 * `PageFocusDirective` —el `visibilitychange` del WebView y el `appStateChange`
 * de Capacitor—, así que sin esto el contador bajaría de dos en dos. Un
 * abandono de verdad pide salir, volver y volver a salir, que no ocurre en un
 * segundo y medio.
 */
export const ROLEPLAY_ABANDONO_REBOTE_MS = 1500;

/**
 * Cada cuánto se muestrea la cámara para el análisis de expresiones (solo
 * modalidad video). El reloj arranca recién con el saludo del personaje a la
 * vista, no al entrar a la página.
 */
export const ROLEPLAY_FRAME_INTERVAL_MS = 4000;

/** Ancho al que se reescala el frame antes de exportarlo; el alto es proporcional. */
export const ROLEPLAY_FRAME_MAX_WIDTH = 480;

/** Calidad del JPEG: suficiente para leer la cara sin inflar el payload. */
export const ROLEPLAY_FRAME_QUALITY = 0.7;

// ─────────────────────────────────────────────────────────────────────────────
// Contrato de backend (DTOs)
//
// Forma literal de las respuestas de la API del roleplay. NO renombrar llaves:
// esta sección es la que se contrasta contra la documentación de backend, y es
// también lo que `RoleplayMockService` reproduce mientras los endpoints no
// existan.
// ─────────────────────────────────────────────────────────────────────────────

/** Motivo por el que la API ordena cerrar la conversación. */
export type RoleplayEndReason = 'time_limit' | 'turn_limit';

/** Escenario del roleplay tal como llega dentro del tema (`temas/v2`). */
export interface TopicRoleplayScenarioDto {
  id: string;
  /** Ej: "Atención a Cliente Molesto por Demora". */
  title: string;
}

/**
 * Bloque `roleplay` del tema. Sale de `SummaryTopic`, en la misma respuesta que
 * `evaluable` / `tipo_ev` / `intentos_restantes` de la evaluación teórica, así
 * que no cuesta una llamada aparte.
 *
 * **No trae un `estado`**: se deriva de `attempts` (gastó un intento ⇒ ya la
 * rindió) y `passed`. Ver `RoleplayService.isDone()`. Si además se puede volver
 * a dar lo decide `puedeIntentar()`, que cruza los intentos con
 * `allow_practice_after_pass`.
 */
export interface TopicRoleplayDto {
  /**
   * Redundante —si no hay roleplay, backend manda el objeto entero en null—
   * pero se valida igual, por si algún día cambia esa lógica.
   */
  has: boolean;
  /**
   * La práctica genera nota. Si es false es solo un paso a completar para dar
   * el tema por terminado: no hay `passed` ni llaves de nota.
   */
  is_evaluable: boolean;
  /** 'separate' | 'averaged_with_exam'. */
  grade_mode: string;
  /** Derivada de `grade_mode`; la manda backend ya calculada. */
  is_averaged: boolean;
  /** Modalidad en la que responde el usuario. */
  student_mode: RoleplayMode | string;
  /**
   * Tope de intentos. `null` o `0` = sin intentos: **no se puede dar**, mismo
   * criterio que `intentos_restantes` en la teórica.
   */
  max_attempts: number | null;
  /** Intentos ya gastados. `> 0` ⇒ el roleplay ya se rindió. */
  attempts: number;
  /** null = todavía no se evaluó ninguna práctica. */
  passed: boolean | null;
  /**
   * Configuración del espacio: si es `false`, un roleplay ya aprobado **no se
   * puede repetir aunque le queden intentos**.
   *
   * Opcional a propósito: ausente ⇒ se asume permitido, que es el
   * comportamiento previo a que backend mandara la llave. Quien la interpreta
   * es `RoleplayService.cerradoPorAprobacion()`.
   */
  allow_practice_after_pass?: boolean;
  scenario: TopicRoleplayScenarioDto;

  // ── TODO(api): pedidas a backend, todavía no llegan ────────────────────────
  // Solo tienen sentido con `is_evaluable: true`. Opcionales a propósito: sin
  // ellas la vista degrada (no pinta nota) en vez de romperse.

  /** Nota obtenida, en la escala del tema (ej. 17 de 20). */
  grade?: number | null;
  /** Nota mínima para aprobar. */
  min_grade?: number | null;
  /** Nota máxima de la escala. */
  max_grade?: number | null;
}

/**
 * Ids del usuario y su workspace.
 *
 * Ninguna API del roleplay los pide ya: backend los resuelve desde la sesión y
 * todos estos bodies van vacíos. Sobreviven solo para `RoleplayMockService`,
 * que los necesita para devolver un resultado con datos coherentes.
 */
export interface RoleplayIds {
  customer_id: string;
  user_id: string;
  workspace_id: string;
}

/** Personaje del escenario, tal cual lo manda backend. */
export interface RoleplayPersonaDto {
  /** Ej: "Carlos". */
  name: string;
  /** Ej: "Gerente de compras escéptico". */
  role_description: string;
  personality_traits: string[];
}

/** Escenario del roleplay configurado en el tema. */
export interface RoleplayScenarioDto {
  id: string;
  title: string;
  scenario_context: string;
  learning_objectives: string[];
  difficulty: RoleplayDifficulty;
  max_turns: number;
  max_duration_seconds: number;
  persona: RoleplayPersonaDto;
}

/**
 * Reglas de rendición del tema, tal cual las manda backend.
 *
 * Es **el mismo bloque** que recibe la evaluación teórica en su payload (lo
 * consume sin tipar en `evaluacion.page.ts`): la configuración es del tema, no
 * de un tipo de evaluación, así que las dos la reciben igual y con las mismas
 * llaves. De acá el roleplay solo usa las dos del control de abandono; las
 * otras son de preguntas y se declaran para que el bloque quede completo.
 */
export interface RoleplayEvaluationSettingsDto {
  /** Enciende el control de abandono: salir de la vista pasa a descontar. */
  not_allowed_change_page?: boolean;
  /**
   * Cuántas salidas se toleran antes de cerrar la evaluación. Con `2`, la
   * primera y la segunda avisan y la **tercera** bloquea.
   *
   * ⚠️ El `boolean` no es un descuido: backend manda `false` cuando no está
   * configurado. `Number(false)` es `0`, así que ese caso cae solo en el mapeo
   * y equivale a bloquear en la primera salida — mismo comportamiento que la
   * teórica ante el mismo payload.
   */
  quantity_attempts_to_change_page?: number | boolean;
  /** De preguntas: el roleplay la ignora. */
  show_question_by_question?: boolean;
  /** De preguntas: el roleplay la ignora. */
  not_shuffle_quiz_options?: boolean;
}

/**
 * Respuesta de `GET roleplay/topics/{topic_id}` y de su equivalente para los
 * roleplays libres, `GET roleplay/free/{id}/config`.
 *
 * Es la configuración del roleplay, no una sesión: se puede pedir cuantas veces
 * haga falta y no consume intento.
 */
export interface RoleplayTopicResponseDto {
  is_evaluable: boolean;
  /** Ej: "averaged_with_exam". */
  grade_mode: string;
  /** Modalidad en la que responde el usuario. */
  student_mode: RoleplayMode;
  scenario: RoleplayScenarioDto;
  /**
   * Reglas de rendición del tema; de acá sale el control de abandono de la
   * vista. Opcional: un espacio que no lo configure lo deja apagado en vez de
   * romper la pantalla.
   */
  evaluation_settings?: RoleplayEvaluationSettingsDto;
}

/**
 * Body con el que `RoleplayMockService` abre una sesión.
 *
 * La API real no lo usa: `POST roleplay/topics/{topic_id}/sessions` va con el
 * body vacío y el tema en la URL. Sobrevive solo mientras exista el mock, que
 * necesita `mode` para elegir guion.
 */
export interface RoleplayStartRequestDto extends RoleplayIds {
  scenario_id: string;
  mode: RoleplayMode;
}

/**
 * Respuesta de `POST roleplay/topics/{topic_id}/sessions` y de la de los
 * libres, `POST roleplay/free/{id}/sessions`: las dos devuelven lo mismo.
 */
export interface RoleplayStartResponseDto {
  session_id: string;
  /**
   * Primer turno del personaje. `audio_url` es null en modalidad chat.
   *
   * ⚠️ **El bloque entero llega null en `streaming`**: ahí el saludo no es un
   * turno pregrabado, lo genera el personaje por el canal en vivo.
   */
  opening: { text: string; audio_url: string | null } | null;
  mode: RoleplayMode;
  /** Número máximo de interacciones. */
  max_turns: number;
  /** Ventana de tiempo de la conversación, en segundos. */
  max_duration_seconds: number;
}

/**
 * Body de `POST roleplay/sessions/{id}/turns-text`: solo el texto del usuario.
 * Backend ya tiene los ids a partir de la sesión.
 */
export interface RoleplayTextTurnRequestDto {
  text: string;
}

/**
 * Respuesta de los dos endpoints de turno (`turns-text` y `turns`).
 *
 * ⚠️ `turn_index` cuenta TODOS los mensajes (personaje + usuario), así que
 * avanza de dos en dos por intercambio: con `max_turns: 6` el cierre por
 * `turn_limit` llega en 12. Ver `RoleplayService.interaccionesDesde()`.
 */
export interface RoleplayTurnResponseDto {
  trainee_transcript: string;
  persona_text: string;
  persona_audio_url: string | null;
  turn_index: number;
  session_ended: boolean;
  end_reason: RoleplayEndReason | null;
}

/**
 * Body de `POST roleplay/sessions/{id}/frames`.
 *
 * Cada elemento es un JPEG en base64 **puro**: sin el prefijo
 * `data:image/jpeg;base64,` que devuelve `canvas.toDataURL()`.
 */
export interface RoleplayFramesRequestDto {
  frames: string[];
}

/**
 * Respuesta de `POST roleplay/sessions/{id}/frames`.
 *
 * Se recibe y no se pinta: el análisis de expresiones llega al usuario dentro
 * del resultado final (`RoleplayEndResponseDto.expressions`), no durante la
 * conversación. La forma exacta de `expressions` la define backend; aquí solo
 * se fija `score` porque es lo único documentado.
 */
export interface RoleplayFramesResponseDto {
  expressions: { score?: number; [key: string]: any } | null;
}

/** Un criterio de la rúbrica dentro del resultado. */
export interface RoleplayCriterionScoreDto {
  key: string;
  label: string;
  /** 0..1 */
  score: number;
  /** Peso del criterio en la nota final, 0..1. */
  weight: number;
  rationale: string;
  evidence_quotes: string[];
  how_to_improve: string;
  example_better_response: string;
}

/**
 * Un turno dentro de la transcripción que devuelve `/end`.
 *
 * Es la conversación ya cerrada, no los mensajes en vivo: por eso `role` usa el
 * vocabulario del backend (`persona` / `trainee`) en vez del `autor` de
 * `RoleplayMessage`. `RoleplayService` traduce entre los dos.
 */
export interface RoleplayTranscriptTurnDto {
  /** Posición del turno en la conversación, contando a los dos interlocutores. */
  turn_index?: number;
  role: 'persona' | 'trainee';
  text: string;
  /** Audio del turno del personaje; null en chat y en los turnos del usuario. */
  audio_url: string | null;
  /** ISO string. */
  created_at: string;
  /** El texto es la transcripción de una grabación (modalidades voz y video). */
  is_transcript?: boolean;
}

/**
 * Puntuación cualitativa de una dimensión del resultado (entonación,
 * expresiones).
 *
 * Backend la manda como bloque: la nota más el detalle con el que la calculó.
 * `aspects` es un diccionario abierto —sus llaves cambian según la dimensión
 * (`pace`, `energy`… en entonación; `eye_contact`, `smile`… en expresiones)—,
 * así que se tipa como tal.
 */
export interface RoleplayScoreBlockDto {
  /** 0..1 */
  score: number;
  summary?: string;
  aspects?: Record<string, string>;
  /** Modelo que emitió la puntuación. Ej: "gpt-audio-mini". */
  model?: string;
}

/**
 * Respuesta de `POST roleplay/sessions/{id}/end`.
 *
 * ⚠️ TODO(api): todo lo marcado como opcional acá abajo **todavía no lo manda
 * backend**. Está confirmado que lo agregará, y hasta entonces lo produce
 * `RoleplayMockService` para que la pantalla de resultados sea recorrible. Son
 * opcionales a propósito: cuando el payload real llegue incompleto, la vista
 * degrada en vez de romperse.
 */
export interface RoleplayEndResponseDto {
  session_id: string;
  scenario_id: string;
  workspace_id: string;
  customer_id: string;
  user_id: string;
  /** 0..1 */
  overall_score: number;
  /** 0..1 */
  rubric_score: number;
  /**
   * Solo en modalidades con audio; null en chat.
   *
   * ⚠️ La API manda el bloque completo; el `number` de la unión es la forma
   * vieja, que todavía produce `RoleplayMockService`.
   */
  intonation: RoleplayScoreBlockDto | number | null;
  /** Solo en modalidad video; null en el resto. Misma unión que `intonation`. */
  expressions: RoleplayScoreBlockDto | number | null;
  passed: boolean;
  /** Nota mínima para aprobar, 0..1. */
  passing_threshold: number;
  criteria_scores: RoleplayCriterionScoreDto[];
  strengths: string[];
  areas_for_improvement: string[];
  summary_feedback: string;
  scored_by_model: string;
  /** Id del registro de resultado. */
  id: string;

  // ── Transcripción y métricas de la sesión ──────────────────────────────────
  // Alimentan la pestaña "Ver chat transcrito". No se persisten en el front: el
  // resultado tiene que poder reabrirse más tarde, cuando la sesión ya no está
  // en memoria.

  /** Conversación completa, en orden cronológico. */
  transcript?: RoleplayTranscriptTurnDto[];
  /** Interacciones que gastó el usuario. Ej: 4 de 6. */
  turns_used?: number;
  max_turns?: number;
  /** Duración real de la conversación, en segundos. */
  elapsed_seconds?: number;
  /** Ventana que tenía disponible, en segundos. */
  max_duration_seconds?: number;

  // ── Quién habla ────────────────────────────────────────────────────────────
  // Hacen falta para encabezar las burbujas fuera de la sesión: el personaje
  // llega por `GET roleplay/topics/{id}`, que la pantalla de resultados no pide.

  /** Ej: "Carlos Ramírez". */
  persona_name?: string;
  /** Ej: "Cliente frustrado". */
  persona_role_description?: string;
  /** Rol que interpretó el usuario. Ej: "Asesor de tienda". */
  trainee_role?: string;

  // ── Cabecera del resultado ─────────────────────────────────────────────────
  // Mismo rol que las llaves homónimas de la evaluación teórica
  // (`evaluar_preguntas`): la pantalla de resultados es la misma maqueta.

  attempts_used?: number;
  max_attempts?: number;
  /** Nota en la escala del tema (ej: 9 de 20), aparte del `overall_score` 0..1. */
  grade?: number;
  maximun_grade?: number;
  /**
   * ⚠️ Estas dos no llegan siempre de la API. Mientras backend no las mande, las
   * rellena quien navega a la pantalla de resultados con los nombres que traía
   * por la URL (`courseName` / `topicName`): `EvaluacionPracticaPage` al guardar
   * el resultado, y `RoleplayResultadosPage` al releerlo. La API gana en cuanto
   * empiece a mandarlas.
   *
   * En un roleplay `free` no hay curso ni tema: solo se puede poner el título
   * del escenario en `topic_name`, y `course_name` se queda sin definir.
   */
  topic_name?: string;
  course_name?: string;
  /** ISO string. */
  evaluated_at?: string;
  /** Habilita el acceso al detalle ("Conoce tus respuestas"). */
  active_results?: boolean;
  /** QR de verificación, ya renderizado por backend. */
  validator?: { image_qr: string; identifier: string };

  // ── Botonera ───────────────────────────────────────────────────────────────

  /**
   * Id del tema siguiente, si lo hay. Mismo rol que `tema_siguiente` en la
   * evaluación teórica: es un id, no un booleano.
   */
  next_topic_id?: number | string | null;
  /** Id del curso siguiente, si lo hay. */
  next_course_id?: number | string | null;
  show_certification_to_user?: boolean;
  pending_survey?: boolean;
}

/**
 * Datos del intento dentro de `GET roleplay/sessions/{id}/results`.
 *
 * Es el equivalente de las llaves de cabecera que `/end` manda planas
 * (`grade`, `attempts_used`…), pero acá backend las agrupa bajo `evaluation` y
 * con su propia nomenclatura, que se respeta tal cual llega.
 */
export interface RoleplaySessionEvaluationDto {
  /** Id con el que se verifica el intento; el mismo que muestra el QR. */
  identifier?: string;
  /** Nota en la escala del tema. Ej: 12.04 de 20. */
  grade?: number;
  grade_scale?: number;
  /** Nota y mínimo en porcentaje, ya redondeados por backend. */
  score_percentage?: number;
  passing_percentage?: number;
  passed?: boolean;
  /** ISO string. */
  evaluated_at?: string;
  attempts_taken?: number;
  max_attempts?: number;
  validator?: { image_qr: string; identifier: string } | null;
}

/**
 * Respuesta de `GET roleplay/sessions/{id}/results`.
 *
 * El mismo cuerpo de `/end` —rúbrica, transcripción y todo— más el bloque
 * `evaluation`. A diferencia de `/end` no cierra ni puntúa nada: relee lo que
 * quedó guardado, y es de donde sale hoy la pestaña "Ver chat transcrito".
 */
export interface RoleplaySessionResultsDto extends RoleplayEndResponseDto {
  evaluation?: RoleplaySessionEvaluationDto;
}

/** `RoleplaySessionResultsDto` ya en forma de UI, como `RoleplayResult`. */
export interface RoleplaySessionResult extends RoleplayResult {
  evaluation?: RoleplaySessionEvaluationDto;
}

// ─────────────────────────────────────────────────────────────────────────────
// Modalidad streaming (conversación en vivo)
//
// Solo dos endpoints, y ninguno transporta audio: backend entrega el token
// efímero con el que el navegador abre el canal WebRTC **directo contra
// OpenAI**, y después recibe el registro de cada intercambio para poder puntuar
// la sesión al cerrarla. El `/end` y su rúbrica son los mismos de las otras
// modalidades.
// ─────────────────────────────────────────────────────────────────────────────

/** Quién habla en un turno reportado a `realtime-turn`. */
export type RoleplayRealtimeRole = 'trainee' | 'persona';

/**
 * Respuesta de `POST roleplay/sessions/{id}/realtime-token`.
 *
 * ⚠️ `client_secret` es de **un solo uso** y caduca (`expires_at`): sirve para
 * una negociación SDP y nada más. Reconectar exige pedir otro.
 *
 * Devuelve 400 cuando el escenario no soporta streaming (voz y persona tienen
 * que ser ambos de OpenAI); en ese caso el `detail` del error es lo que se le
 * muestra al usuario.
 */
export interface RoleplayRealtimeTokenDto {
  /** Token efímero de OpenAI. Empieza con `ek_`. */
  client_secret: string;
  /** Unix epoch en segundos. */
  expires_at: number;
  /** Modelo realtime con el que abrir el canal. Ej: "gpt-realtime". */
  model: string;
  /** Voz configurada para el personaje. Ej: "alloy". Informativa: la sesión ya viene armada. */
  voice: string;
}

/**
 * Respuesta de `POST roleplay/sessions/{id}/realtime-turn`.
 *
 * ⚠️ Su `turn_index` **no** es el de `RoleplayTurnResponseDto`, y confundirlos
 * es lo que dejaba el contador del header a la mitad:
 *
 * - `turns` / `turns-text`: cuenta los mensajes de **ambos** hablantes, así que
 *   avanza de dos en dos por intercambio (`max_turns: 12` cierra en 24).
 * - `realtime-turn`: cuenta solo los turnos **del usuario** y es **0-based**,
 *   así que el turno 12 de 12 llega como `turn_index: 11` con
 *   `end_reason: 'turn_limit'`.
 *
 * Ver `RoleplayService.interaccionesDesdeRealtime()`. Qué índice devuelven los
 * reportes del personaje no está definido, así que el contador solo se alimenta
 * de los del `trainee`.
 */
export interface RoleplayRealtimeTurnResponseDto {
  turn_index: number;
  session_ended: boolean;
  end_reason: RoleplayEndReason | null;
  /**
   * El personaje se salió de su rol. Backend manda en `role_reminder` el texto
   * con el que hay que recordárselo a la sesión viva; no se le muestra nada al
   * usuario.
   */
  role_drift: boolean;
  role_reminder: string;
}

/**
 * Endpoint de OpenAI al que se le manda la oferta SDP.
 *
 * La negociación es **directa navegador ↔ OpenAI**: no pasa por el backend de
 * Cursalab, y por eso va con `fetch()` crudo y no con `HttpClient` (los
 * interceptors le agregarían token, query params y `Content-Type` propios).
 */
export const ROLEPLAY_REALTIME_SDP_URL = 'https://api.openai.com/v1/realtime/calls';

/** Nombre que OpenAI espera en el data channel de eventos. No es libre. */
export const ROLEPLAY_REALTIME_CHANNEL = 'oai-events';

// ─────────────────────────────────────────────────────────────────────────────
// El roleplay como actividad propia (vista "Roleplays")
//
// Segunda puerta al MISMO roleplay que ya se da desde el tema: el listado agrupa
// los que no cuelgan de ningún curso (`free`) junto a los que sí (`topic`).
//
// El listado sale de `GET roleplay/mine-all`, y su vocabulario es el que se usa
// de acá en adelante sin traducir: `free`/`topic` para los grupos y
// `pending|in_progress|passed|failed` para el estado.
//
// Los del grupo `topic` dan la práctica por el endpoint del tema
// (`roleplay/topics/{topic_id}/sessions`, el mismo del otro flujo): es backend
// quien descuenta el intento y actualiza la nota, así que cuando el usuario
// entre después al tema, su propia API ya responde actualizada.
//
// Los `free` tienen los suyos, con el `id` del item: `GET roleplay/free/{id}/config`
// para el escenario y `POST roleplay/free/{id}/sessions` para abrir la sesión.
// De ahí en adelante los dos grupos comparten todo, porque el resto del flujo
// (`/turns`, `/frames`, `/end`, `/results`) va por `session_id`.
// ─────────────────────────────────────────────────────────────────────────────

/** Grupo del que cuelga el roleplay. Es lo que decide en qué bloque se pinta. */
export type RoleplayMineAllGrupo = 'free' | 'topic';

/** Estado por el que se puede filtrar el listado, en vocabulario de la API. */
export type RoleplayMineAllStatus = 'pending' | 'in_progress' | 'passed' | 'failed';

/**
 * Filtros del listado, todos opcionales y combinables. Sin ninguno, la API
 * devuelve los dos grupos completos.
 */
export interface RoleplayMineAllFiltros {
  type?: RoleplayMineAllGrupo;
  status?: RoleplayMineAllStatus;
  /** Solo los evaluables. Viaja como `evaluable=1`, y solo cuando es `true`. */
  evaluable?: boolean;
}

/** Un roleplay del listado, tal cual lo manda backend. */
export interface RoleplayMineAllItemDto {
  /** De dónde cuelga. Es la misma llave por la que agrupa `data`. */
  source: RoleplayMineAllGrupo;
  /**
   * Id de la asignación, y **el id con el que se abre la sesión en `free`**
   * (`roleplay/free/{id}/sessions`). En `topic` no lo es: ahí manda `topic_id`.
   * Se repite entre los dos grupos, así que solo identifica junto a `source`.
   */
  id: number;
  scenario_id: string;
  /** Título del escenario. Ej: "Manejo de Conflictos en el Trabajo". */
  title: string;
  /** Curso del que cuelga. Se pinta encima del título; en `free` llega null. */
  course_name: string | null;
  /**
   * Tema al que pertenece. Es el id con el que se abre la sesión en el grupo
   * `topic` (`roleplay/topics/{topic_id}/sessions`).
   *
   * En `free` no hay tema y llega `null`: esos abren su sesión con `id`.
   */
  topic_id: number | null;
  /** Modalidad en la que responde el usuario: decide icono y color de la tarjeta. */
  student_mode: RoleplayMode;
  /** Tope de intentos. `null` o `0` = sin tope configurado. */
  max_attempts: number | null;
  is_evaluable: boolean;
  /** Nota final, **vigesimal y como string**: "13.20". null si no rindió. */
  grade: string | null;
  /** Intentos gastados. Espejo de `attempts_taken`. */
  attempts: number;
  /** null = todavía no se evaluó ningún intento. */
  passed: boolean | null;
  allow_practice_after_pass: boolean;
  /** Estado con el que se pinta la píldora, ya derivado por backend. */
  status: RoleplayMineAllStatus;
  attempts_taken: number;
  attempts_remaining: number;
  /**
   * Puede volver a practicar. Ya combina los intentos que quedan con
   * `allow_practice_after_pass`, así que el front no rehace esa cuenta.
   */
  can_practice: boolean;
  /**
   * Hay resultados guardados que se puedan releer. Es lo que habilita "Ver
   * resultados previos" en el selector de acción.
   */
  has_previous_results: boolean;
  /** Nota del último intento, en **fracción 0–1** (`0.42`). Otra escala que `grade`. */
  last_score: number | null;
  /** Sesión del último intento. Con ella se releen los resultados previos. */
  last_session_id: string | null;
  last_ended_at: string | null;
}

/** Un grupo del listado: su título, cuántos son y los roleplays en sí. */
export interface RoleplayMineAllGrupoDto {
  /** Copy que manda backend para la cabecera del grupo. Ej: "Libres". */
  label: string;
  count: number;
  items: RoleplayMineAllItemDto[];
}

/**
 * Respuesta de `GET roleplay/mine-all`.
 *
 * `data` viene agrupada por tipo (a diferencia del `{ items: [] }` plano que
 * devuelve el mock) y `counts` trae los totales de cada filtro, ya calculados
 * sobre el listado completo aunque la llamada venga filtrada.
 */
export interface RoleplayMineAllResponseDto {
  data: Partial<Record<RoleplayMineAllGrupo, RoleplayMineAllGrupoDto>>;
  /**
   * `all`, `free`, `topic`, `evaluable`, `pending`, `passed`, `failed`. Es lo
   * que numera cada chip, y llega calculado sobre el listado completo aunque la
   * llamada venga filtrada.
   *
   * ⚠️ Hoy NO trae `in_progress`, aunque sea un `status` válido: el chip de ese
   * estado se queda sin número hasta que backend lo agregue.
   */
  counts: Record<string, number>;
}

/**
 * Filtro activo del listado. `todos` no filtra; los demás son los valores que
 * entiende la API, y por eso el chip se traduce a params sin tabla de por medio.
 */
export type RoleplayFiltro =
  | 'todos'
  | RoleplayMineAllGrupo
  | RoleplayMineAllStatus
  | 'evaluable';

/**
 * Qué intento se está viendo en la pantalla de resultados previos.
 *
 * Son las dos pestañas del diseño: el intento con el que aprobó (si existe) y el
 * último que dio, que pueden ser el mismo.
 */
export type RoleplayIntentoVisto = 'aprobado' | 'ultimo';

/**
 * Los dos intentos que muestra "Ver resultados previos", ya mapeados a UI.
 *
 * Cada uno se relee por su `session_id` con
 * `RoleplayService.getResultadoDeSesion()`. El listado hoy solo manda el del
 * último intento (`last_session_id`), así que `aprobado` va siempre en null y
 * esa pestaña queda deshabilitada: falta que backend mande `passed_session_id`.
 */
export interface RoleplayResultados {
  aprobado: RoleplaySessionResult | null;
  ultimo: RoleplaySessionResult | null;
}

// ── vocabulario visual ───────────────────────────────────────────────────────
//
// Copy y clases de UI, no datos: no llegan en ningún payload, igual que
// `ROLEPLAY_INDICACIONES`. Viven acá porque los comparten la tarjeta del
// listado y la cabecera de la pantalla de resultados, y una modalidad tiene que
// verse igual en las dos.

/** Cómo se pinta una modalidad: su etiqueta, su icono y su color. */
export interface RoleplayModalidadUi {
  /** Lo que lee el usuario. Ej: "Texto". */
  etiqueta: string;
  /** Clase de Material Design Icons. */
  icono: string;
  /** Color del icono y de la etiqueta. */
  texto: string;
  /** Fondo del bloque que acompaña a la tarjeta. */
  fondo: string;
}

export const ROLEPLAY_MODALIDAD_UI: Record<RoleplayMode, RoleplayModalidadUi> = {
  chat: {
    etiqueta: 'Texto',
    icono: 'mdi-text-box-outline',
    texto: 'text-primary',
    fondo: 'bg-indigo-100',
  },
  voice: {
    etiqueta: 'Audio',
    icono: 'mdi-volume-high',
    texto: 'text-green-600',
    fondo: 'bg-green-100',
  },
  video: {
    etiqueta: 'Video',
    icono: 'mdi-video',
    texto: 'text-red-600',
    fondo: 'bg-red-100',
  },
  // El diseño no la contempla todavía; se le da identidad propia para que no
  // caiga en el color de otra modalidad si backend la manda.
  streaming: {
    etiqueta: 'En vivo',
    icono: 'mdi-account-voice',
    texto: 'text-orange-500',
    fondo: 'bg-orange-100',
  },
};

/** Texto del estado en la tarjeta; los colores viven en su template. */
export const ROLEPLAY_ESTADO_UI: Record<RoleplayMineAllStatus, { etiqueta: string }> = {
  pending: { etiqueta: 'Pendiente' },
  in_progress: { etiqueta: 'En progreso' },
  passed: { etiqueta: 'Aprobado' },
  failed: { etiqueta: 'Desaprobado' },
};
